<script setup lang="ts">
import type { AcReusableTableColumn } from '@/types'
import { ref, computed, watch, onMounted } from 'vue'

interface Props {
    columns: AcReusableTableColumn[]
    data: any[]
    isSelectable?: boolean
    sortDirection?: 'asc' | 'desc'
    sortColumn?: string
    filteredTerm?: string
    filterCols?: string[]
    /* eslint-disable */
    itemKey?: string // Key to identify unique items (default: 'id')
    rowClicked?: (item: any) => void
}

const props = withDefaults(defineProps<Props>(), {
    isSelectable: false,
    sortDirection: 'asc',
    sortColumn: '',
    filteredTerm: '',
    filterCols: () => [],
    itemKey: 'id'
})

const emit = defineEmits<{
    columnSorted: [column: string]
    selectionChanged: [selectedItems: any[]]
    rowClicked: [item: any]
}>()

// Selection state
const selectedItems = ref<any[]>([])
const isAllSelected = ref(false)

// Computed properties
const filteredData = computed(() => {
    if (!props.filteredTerm || props.filterCols.length === 0) {
        return props.data
    }
    
    return props.data.filter(item => {
        return props.filterCols.some(colKey => {
            const value = getNestedValue(item, colKey)
            return String(value).toLowerCase().includes(props.filteredTerm.toLowerCase())
        })
    })
})

const sortedData = computed(() => {
    if (!props.sortColumn) {
        return filteredData.value
    }
    
    const sorted = [...filteredData.value].sort((a, b) => {
        const aValue = getNestedValue(a, props.sortColumn)
        const bValue = getNestedValue(b, props.sortColumn)
        
        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return aValue.localeCompare(bValue)
        }
        
        if (aValue < bValue) return -1
        if (aValue > bValue) return 1
        return 0
    })
    
    return props.sortDirection === 'desc' ? sorted.reverse() : sorted
})

// Helper function to get nested object values (e.g., 'client.name')
const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj)
}

// Selection methods
const handleItemSelection = (item: any, isSelected: boolean) => {
    const itemId = item[props.itemKey]
    
    if (isSelected) {
        if (!selectedItems.value.some(selected => selected[props.itemKey] === itemId)) {
            selectedItems.value.push(item)
        }
    } else {
        selectedItems.value = selectedItems.value.filter(selected => selected[props.itemKey] !== itemId)
    }
    
    updateSelectAllState()
    emit('selectionChanged', selectedItems.value)
}

const handleSelectAll = () => {
    if (isAllSelected.value) {
        selectedItems.value = [...sortedData.value]
    } else {
        selectedItems.value = []
    }
    
    emit('selectionChanged', selectedItems.value)
}

const updateSelectAllState = () => {
    const visibleItemsCount = sortedData.value.length
    const selectedVisibleItems = selectedItems.value.filter(selected => 
        sortedData.value.some(visible => visible[props.itemKey] === selected[props.itemKey])
    ).length
    
    isAllSelected.value = visibleItemsCount > 0 && selectedVisibleItems === visibleItemsCount
}

const isItemSelected = (item: any): boolean => {
    return selectedItems.value.some(selected => selected[props.itemKey] === item[props.itemKey])
}

// Sorting methods
const handleColumnSort = (column: AcReusableTableColumn) => {
    if (column.isSortable) {
        emit('columnSorted', column.key)
    }
}

const handleRowClick = (item: any) => {
    emit('rowClicked', item)
}

// Watch for changes in filtered data to update selection state
watch(sortedData, () => {
    updateSelectAllState()
}, { deep: true })

// Watch for external selection changes
watch(() => props.data, () => {
    // Reset selection when data changes
    selectedItems.value = []
    isAllSelected.value = false
    emit('selectionChanged', selectedItems.value)
}, { deep: true })

// Debug logging
onMounted(() => {
    console.log('AcDataTable mounted')
    console.log('Props columns:', props.columns)
    console.log('Props data:', props.data)
    console.log('Props sortColumn:', props.sortColumn)
    console.log('Props sortDirection:', props.sortDirection)
})
</script>

<template>
    <table class="alt-table" :class="$attrs.class">
        <thead>
            <tr>
                <!-- Selection column -->
                <th v-if="isSelectable" class="w-[2%]">
                    <input 
                        v-model="isAllSelected" 
                        @change="handleSelectAll" 
                        type="checkbox" 
                        class="table-checkbox" 
                    />
                </th>
                
                <!-- Data columns -->
                <th 
                    v-for="column in columns" 
                    :key="column.key"
                    :style="{ width: column.width }"
                    :class="{ 'cursor-pointer select-none': column.isSortable }"
                    @click="handleColumnSort(column)"
                >
                    {{ column.title }}
                    <span 
                        v-if="column.isSortable && sortColumn === column.key" 
                        class="text-lg ml-4"
                    >
                        {{ sortDirection === 'asc' ? '&#9650;' : '&#9660;' }}
                    </span>
                </th>
            </tr>
        </thead>
        
        <tbody>
            <tr 
                v-for="item in sortedData" 
                :key="item[itemKey]"
                class="hover:bg-gray-200 cursor-pointer"
                @click="handleRowClick(item)"
            >
                <!-- Selection cell -->
                <td v-if="isSelectable" @click.stop>
                    <input 
                        :checked="isItemSelected(item)"
                        @change="handleItemSelection(item, ($event.target as HTMLInputElement).checked)"
                        type="checkbox" 
                        class="table-checkbox" 
                    />
                </td>
                
                <!-- Data cells -->
                <td 
                    v-for="column in columns" 
                    :key="column.key"
                    :style="{ width: column.width }"
                    :class="{ 'clickable-cell': !column.preventRowClick }"
                    @click="column.preventRowClick ? $event.stopPropagation() : null"
                >
                    <!-- Slot for custom cell content -->
                    <slot 
                        :name="`cell-${column.key}`" 
                        :item="item" 
                        :value="getNestedValue(item, column.key)"
                        :column="column"
                    >
                        <!-- Default cell content -->
                        {{ getNestedValue(item, column.key) }}
                    </slot>
                </td>
            </tr>
            
            <!-- Empty state -->
            <tr v-if="sortedData.length === 0">
                <td :colspan="columns.length + (isSelectable ? 1 : 0)" class="text-center py-8">
                    <slot name="empty-state">
                        No data available
                    </slot>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<style scoped>
/* Additional styles can be added here if needed */
</style>
